import {
  Action,
  ActionReducerMapBuilder,
  combineReducers,
  configureStore,
  createAction,
  createReducer,
} from '@reduxjs/toolkit';
import { useDispatch, useSelector, TypedUseSelectorHook } from 'react-redux';
import { persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import { cookieService } from '@services/cookie.service';

import authReducer from './state/authSlice';
import channelsReducer from './state/channelsSlice';
import chatReducer from './state/chatSlice';
import feedReducer from './state/feedSlice';
import likesReducer from './state/likesSlice';
import participantsReducer from './state/participantsSlice';
import requestsToSpeakReducer from './state/requestsToSpeakSlice';
import searchReducer from './state/searchSlice';
import showsReducer from './state/showsSlice';
import speakersReducer from './state/speakersSlice';
import teamsReducer from './state/teamsSlice';
import topicsReducer from './state/topicsSlice';
import usersReducer from './state/usersSlice';
import { authApi } from './queries/auth';
import { chatApi } from './queries/chat';
import { likeApi } from './queries/like';
import { showApi } from './queries/show';
import { participantApi } from './queries/participant';
import { userApi } from './queries/user';
import { teamApi } from './queries/team';
import { gifsApi } from './queries/gifs';
import { isValidState, createFreshState, migrateState } from './migrations';

const persistConfig = {
  key: 'root',
  version: 1,
  storage,
  blacklist: [
    'authApi',
    'likeApi',
    'showApi',
    'participantApi',
    'teamApi',
    'userApi',
    'chatApi',
    'gifsApi',
  ],
  migrate: async (state: any, currentVersion: number) => {
    try {
      // If no state or invalid state, start fresh
      if (!state || typeof state !== 'object' || !isValidState(state)) {
        await createFreshState();
        return undefined;
      }

      // Apply version-based migrations (includes version check)
      return migrateState(state, currentVersion);
    } catch (error) {
      console.error(`Migration ${currentVersion} failed: ${error}`);
      await createFreshState();
      return undefined;
    }
  },
};

const appReducer = combineReducers({
  auth: authReducer,
  channels: channelsReducer,
  chat: chatReducer,
  feed: feedReducer,
  likes: likesReducer,
  participants: participantsReducer,
  requestsToSpeak: requestsToSpeakReducer,
  search: searchReducer,
  shows: showsReducer,
  speakers: speakersReducer,
  teams: teamsReducer,
  topics: topicsReducer,
  users: usersReducer,
  [authApi.reducerPath]: authApi.reducer,
  [chatApi.reducerPath]: chatApi.reducer,
  [likeApi.reducerPath]: likeApi.reducer,
  [showApi.reducerPath]: showApi.reducer,
  [participantApi.reducerPath]: participantApi.reducer,
  [teamApi.reducerPath]: teamApi.reducer,
  [userApi.reducerPath]: userApi.reducer,
  [gifsApi.reducerPath]: gifsApi.reducer,
});

export type AppState = ReturnType<typeof appReducer>;

export const logoutAction = createAction('LOGOUT');

const rootReducer = createReducer(
  appReducer(undefined, { type: '@@INIT' }),
  (builder: ActionReducerMapBuilder<AppState>) => {
    builder.addCase(logoutAction, (state: AppState, action: Action) => {
      localStorage.removeItem('persist:root');
      cookieService.removeAuthToken();
      const newState = appReducer(undefined, action);
      return {
        ...newState,
      };
    });
    builder.addDefaultCase((state: AppState, action: Action) =>
      appReducer(state, action),
    );
  },
);

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
      },
    }).concat(
      authApi.middleware,
      chatApi.middleware,
      likeApi.middleware,
      showApi.middleware,
      participantApi.middleware,
      teamApi.middleware,
      userApi.middleware,
      chatApi.middleware,
      gifsApi.middleware,
    ),
});

export const persistor = persistStore(store);

export type AppDispatch = typeof store.dispatch;

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<AppState> = useSelector;
